import React, { useState, useEffect, useRef } from "react";

import useHoursLogCtx, {
  HoursLogContext,
} from "../../context/page/HoursLogContext";

import {
  WeeklyChecklist,
  DailyHoursLogForm,
} from "../../features/timekeeping/hoursLog";
import useWeeklyCtx, {
  WeeklyContext,
  useWeeklyContext,
} from "../../context/global/WeeklyContext";

import { submitHoursLog, getHoursLogByWeek } from "../../services/api/hourslog";

import { BACKGROUND_VARIANTS } from "../../config";

import { getWorkersByJob } from "../../utils/business";
import { getWeekStart, getDateString } from "../../utils/datetime";

import { BasicCard, Button } from "../../components/ui/";

/**
 * Hours Log Page Component
 *
 * Allows users to log daily hours for workers across multiple jobs.
 * Features:
 * - Week and day navigation (handled by WeekNavigation and DayNavigation)
 * - Job selection and worker time entry (handled by DailyHoursLogForm)
 * - Weekly log checklist view (handled by WeeklyChecklist)
 *
 * This component focuses on coordinating the subcomponents.
 */
const HoursLogInner = () => {
  const hoursLogCtx = useHoursLogCtx();
  const { date, jobs, scopes } = useWeeklyContext();
  const [showJobsWithoutWork, setShowJobsWithoutWork] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existinglogs, setExistinglogs] = useState(null);
  const [isLoadingLogsData, setIsLoadingLogsData] = useState(true);
  const [loadedFromDB, setLoadedFromDB] = useState(false);
  const [jobsReady, setJobsReady] = useState(false);
  const jobsWithWork = useRef([]);
  const jobsWithoutWork = useRef([]);

  var jobFormsData = {};

  document.title = "Hours Log - R&S";

  // Fetch existing hours log data for the week
  useEffect(() => {
    /**
     * Loads hours-log rows for the week that contains `date`.
     *
     * @returns {Promise<void>}
     */
    const fetchExistingHoursData = async () => {
      setIsLoadingLogsData(true);
      try {
        const weekStartDate = getWeekStart(new Date(date));
        const weekStartString = getDateString(weekStartDate);
        const response = await getHoursLogByWeek(weekStartString);
        setExistinglogs(response);
      } catch (error) {
        console.error("Error fetching existing hours data:", error);
        setExistinglogs(null);
      } finally {
        setIsLoadingLogsData(false);
      }
    };

    if (date) {
      fetchExistingHoursData();
    }
  }, [date]);

  useEffect(() => {
    const formattedJobs =
      scopes && jobs ? getWorkersByJob(jobs, scopes, date) : [];

    // Reset jobs ready state
    setJobsReady(false);

    // Determine which jobs data to use
    if (existinglogs && existinglogs.data[date.getDay()].length > 0) {
      // Use existing hours log data
      setLoadedFromDB(true);
      jobsWithWork.current = existinglogs.data[date.getDay()];

      jobsWithoutWork.current = formattedJobs.filter((job) => {
        // Check if the workers array is empty or doesn't exist
        // and job.id is not in jobsWithWork
        const hasNoWorkers = !job.workers || job.workers.length === 0;
        const isNotInJobsWithWork = !jobsWithWork.current.some(
          (j) => j.id === job.id
        );
        return hasNoWorkers && isNotInJobsWithWork;
      });
    } else {
      setLoadedFromDB(false);
      jobsWithWork.current = formattedJobs.filter((job) => {
        // Check if the workers array has any workers
        return job.workers && job.workers.length > 0;
      });

      jobsWithoutWork.current = formattedJobs.filter((job) => {
        // Check if the workers array is empty or doesn't exist
        // and job.id is not in jobsWithWork
        const hasNoWorkers = !job.workers || job.workers.length === 0;
        const isNotInJobsWithWork = !jobsWithWork.current.some(
          (j) => j.id === job.id
        );
        return hasNoWorkers && isNotInJobsWithWork;
      });
    }

    // Mark jobs as ready to render (as job arrays are refs to avoid re-rendering on
    // sub component updates)
    jobsWithWork.current.length > 0 && setJobsReady(true);
  }, [existinglogs, date, jobs, scopes]);

  // Function to collect data from individual DailyHoursLogForm components
  /**
   * Collects one field from a DailyHoursLogForm into the submit payload map.
   *
   * @param {number|string} jobId Job id.
   * @param {*} formData Field value.
   * @param {string} field Field key (e.g. workers).
   * @returns {void}
   */
  const handleFormDataUpdate = (jobId, formData, field) => {
    if (!jobFormsData[jobId]) {
      jobFormsData[jobId] = {};
    }
    jobFormsData[jobId][field] = formData;
  };

  // Submit form data
  /**
   * Posts collected job/worker hours for the selected date.
   *
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Convert collected data to the required format
      const formData = {
        date: getDateString(date), // Convert to YYYY/MM/DD format
        jobs: Object.entries(jobFormsData)
          .filter(
            ([jobId, data]) => data && data.workers && data.workers.length > 0
          )
          .map(([jobId, data], index) => ({
            id: parseInt(jobId),
            index: index + 1,
            totalHours: data.totalHours,
            workers: data.workers.map((worker) => ({
              id: parseInt(worker.worker),
              timeIn: worker.timeIn,
              timeOut: worker.timeOut,
              hours: parseFloat(worker.hours),
            })),
          })),
      };

      // Filter out jobs with no workers or invalid worker IDs
      formData.jobs = formData.jobs.filter(
        (job) =>
          Array.isArray(job.workers) &&
          job.workers.length > 0 &&
          job.workers.some(
            (worker) =>
              worker.id !== undefined && worker.id !== null && !isNaN(worker.id)
          )
      );

      const response = await submitHoursLog(formData);

      if (response.status === "success") {
        window.location.reload();
      } else {
        throw new Error("Failed to submit hours log");
      }
    } catch (error) {
      console.error("Error submitting hours log:", error);
      alert(
        "Error submitting hours log. Please try again. If the issue persists, please contact the app team."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <HoursLogContext.Provider value={hoursLogCtx}>
      <div className="p-2 pb-24 max-w-[1024px] mx-auto">
        <BasicCard
          bg={BACKGROUND_VARIANTS.light.bg}
          text="Daily Hours Log"
          className="mt-2 font-bold text-lg p-4 mb-2"
        />
        <WeeklyChecklist logs={existinglogs?.data} />
        {loadedFromDB && (
          <BasicCard
            bg={BACKGROUND_VARIANTS.light.bg}
            text="Hours Log Completed"
            className="mt-2 font-bold p-4 mb-6"
          />
        )}
        {/* Loading state */}
        {isLoadingLogsData || !jobsReady ? (
          <div className="text-center py-8">
            <div className="text-gray-600">
              {isLoadingLogsData
                ? "Loading hours data..."
                : "No Workers Scheduled"}
            </div>
          </div>
        ) : null}
        {!isLoadingLogsData && (
          <>
            <div
              className={`${loadedFromDB ? "pointer-events-none opacity-50" : "pointer-events-auto"}`}
            >
              {/* Jobs with work */}
              {jobsWithWork.current.map((job) => (
                <DailyHoursLogForm
                  key={job.id || job.number}
                  jobData={job}
                  onFormDataChange={handleFormDataUpdate}
                  onSubmitSuccess={() =>
                    console.log("Hours logged successfully")
                  }
                />
              ))}
            </div>

            {/* Checkbox to toggle jobs without work */}
            {!loadedFromDB && (
              <div className="flex items-center justify-center my-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showJobsWithoutWork}
                    onChange={(e) => setShowJobsWithoutWork(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-gray-700 font-medium">
                    Show Jobs With No Scheduled Workers
                  </span>
                </label>
              </div>
            )}

            {/* Jobs without work (conditionally rendered) */}

            <div
              className={`${loadedFromDB ? "pointer-events-none opacity-75" : "pointer-events-auto"}`}
            >
              {showJobsWithoutWork &&
                jobsWithoutWork.current.map((job) => (
                  <DailyHoursLogForm
                    key={job.id || job.number}
                    jobData={job}
                    onFormDataChange={handleFormDataUpdate}
                    onSubmitSuccess={() =>
                      console.log("Hours logged successfully")
                    }
                  />
                ))}
            </div>

            {!loadedFromDB && (
              // Submit Button
              <div className="flex justify-center mt-2">
                <Button
                  onClick={handleSubmit}
                  variant={isSubmitting ? "disabled" : "secondary"}
                  text={isSubmitting ? "Submitting..." : "Submit Hours Log"}
                  icon={isSubmitting ? "spinner" : null}
                  className="px-12"
                />
              </div>
            )}
          </>
        )}
      </div>
    </HoursLogContext.Provider>
  );
};

/**
 * Entry point for the Daily Dispatch page app frontend.
 *
 * @component
 */
const HoursLog = () => {
  const weeklyCtx = useWeeklyCtx();

  return (
    <WeeklyContext.Provider value={weeklyCtx}>
      <HoursLogInner />
    </WeeklyContext.Provider>
  );
};

export default HoursLog;
